import React from 'react';
import styled from 'styled-components';
import { DeviceType, ExposureLevel, ModalType, SubscriptionStatus } from '../../../shared/enum/EnumRepository';
import { useModalStore } from '../../../shared/store/portal/modalStore';
import { useAdminMemberListStore, useAdminMemberViewStore } from '../../share/store/adminMemberStore';
import { useDeviceStore } from '../../../shared/store/deviceStore';

const SUBSCRIPTION_OPTIONS = Object.values(SubscriptionStatus);

const FilterStationMember: React.FC = () => {
  const { setStandardModal } = useModalStore();
  const { activeMember } = useAdminMemberViewStore();
  const { filterData, setFilterData } = useAdminMemberListStore();
  const { deviceType } = useDeviceStore();
  const isMobile = deviceType === DeviceType.MOBILE;
  const isActive = isMobile && !!activeMember;

  const getSubscriptionDisplayText = () => {
    const selected = SUBSCRIPTION_OPTIONS.filter((s) => filterData.subscription[s]);
    return selected.length === SUBSCRIPTION_OPTIONS.length ? 'ALL' : selected.join(', ');
  };

  const getIsPublicDisplayText = () => {
    const levels = Object.values(ExposureLevel);
    const selected = levels.filter((l) => filterData.isPublic[l]);
    if (selected.length === levels.length) return 'ALL';
    if (selected.length === 0) return 'None';
    return selected.join(', ');
  };

  const getFilterFactorCount = () => {
    let count = 0;
    if (SUBSCRIPTION_OPTIONS.some((s) => !filterData.subscription[s])) count++;
    if (!Object.values(ExposureLevel).every((l) => filterData.isPublic[l])) count++;
    if (filterData.lastLoginAt.min !== null || filterData.lastLoginAt.max !== null) count++;
    return count;
  };

  const removeFilterFactor = (factor: 'subscription' | 'isPublic' | 'lastLoginAt') => {
    switch (factor) {
      case 'subscription':
        setFilterData({
          ...filterData,
          subscription: Object.fromEntries(
            SUBSCRIPTION_OPTIONS.map((s) => [s, true])
          ) as typeof filterData.subscription,
        });
        break;
      case 'isPublic':
        setFilterData({
          ...filterData,
          isPublic: {
            [ExposureLevel.PUBLIC]: true,
            [ExposureLevel.SEARCHABLE]: true,
            [ExposureLevel.PRIVATE]: true,
          },
        });
        break;
      case 'lastLoginAt':
        setFilterData({
          ...filterData,
          lastLoginAt: { min: null, max: null },
        });
        break;
    }
  };

  const handleFilter = () => {
    setStandardModal({
      modalType: ModalType.FILTER_STATION_MEMBER,
      title: "Filter Members",
      value: "",
      handleChange: () => { },
    });
  };

  return (
    <FilterStationComp $isActive={isActive}>
      <FilterCaption onClick={handleFilter}>
        Filter
      </FilterCaption>
      {getFilterFactorCount() > 0 && <DividerVertical>|</DividerVertical>}
      <FilterFactorContainer>
        {SUBSCRIPTION_OPTIONS.some((s) => !filterData.subscription[s]) && (
          <FilterFactor onClick={() => removeFilterFactor('subscription')}>
            Subscription: {getSubscriptionDisplayText()}
          </FilterFactor>
        )}
        {!Object.values(ExposureLevel).every((l) => filterData.isPublic[l]) && (
          <FilterFactor onClick={() => removeFilterFactor('isPublic')}>
            Exposure: {getIsPublicDisplayText()}
          </FilterFactor>
        )}
        {(filterData.lastLoginAt.min !== null || filterData.lastLoginAt.max !== null) && (
          <FilterFactor onClick={() => removeFilterFactor('lastLoginAt')}>
            Last login: {filterData.lastLoginAt.min ?? 'START'} - {filterData.lastLoginAt.max ?? 'END'}
          </FilterFactor>
        )}
      </FilterFactorContainer>
    </FilterStationComp >
  );
}

const FilterStationComp = styled.section<{ $isActive: boolean }>`
  width: ${({ $isActive }) => $isActive ? '0%' : '100%'};
  opacity: ${({ $isActive }) => $isActive ? 0 : 1};
  height: ${({ $isActive }) => $isActive ? '0%' : 'calc(2vh);'};

  display: flex;
  justify-content: flex-start;

  gap: 10px;
  transition: all 0.7s ease-in-out;
  overflow: hidden;
`;

const FilterCaption = styled.div`
  width: fit-content;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
  user-select: none;
  cursor: pointer;
`;

const FilterFactorContainer = styled.div`
  width: 100%;
  display: flex;
  gap: 15px;
`;

const FilterFactor = styled.div`
  width: fit-content;
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
  user-select: none;
  cursor: pointer;
`;

const DividerVertical = styled.span`
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_04};
`;

export default FilterStationMember;